import constants from '../config/patterns';
/**
 * this utility function facilitates setting values to properties on objects even nested objects
 * for example consider this object.
 *
 * let obj = {
 *  a: {
 *      b: {
 *          c : 1
 *      }
 *   }
 * }
 *
 * if we want to set the nested propery c to say 5, we would call the function like this
 * let boolean = setObjectProperty(obj, "a.b.c", 5);
 *
 *
 * @param {*} obj - the object we want to set a propery on
 * @param {*} prop - the propery name as string, if nested propery use dot separated properties' names, e.g. 'prop1.prop2.prop3'
 * @param {*} val - the value to set the property to.
 * @param {} upsert - a flag to determine the behavior if a property doesn't exist along the chain.
 *                    If true it will add the property else will not continue, it defaults to false.
 * @returns boolean - to indecate success or failer
 */

export function setObjectProperty(obj, prop, val, upsert = false) {
	let props = prop.split('.');
	if (props.length < 1) return false;
	let data = obj;

	// check if any of the props are array indecies, e.g., a.b[1].c, it should enter here like ['a', 'b[1]', 'c'] and leaves
	// like ['a', 'b', 1, 'c']
	let _props: any = [];
	for (let i in props) {
		let _p = props[i].match(constants.extractArrayIndexPattern);
		if (_p) {
			let name = props[i].split(_p[0])[0];
			_props.push(name);
			_props = _props.concat(
				_p.map(a => {
					a = a.split('').slice(1, -1).join('');
					let parsed = parseInt(a);
					if (isNaN(parsed) && a === '$') return a;
					else if (!isNaN(parsed)) return parsed;
					return '';
				})
			);
		} else _props.push(props[i]);
	}

	let pathTofield = data,
		p = _props.shift();

	while (pathTofield && p !== '' && (upsert || pathTofield.hasOwnProperty(p))) {
		if (_props.length === 0) {
			//last prop.
			if (p === '$') pathTofield.push(val);
			//append to an array if $ token is provided
			else pathTofield[p] = val;
			return true;
		}

		/**
		 * if the current property to look into is '$' ,
		 * then we need to create a new object or array and push it to the current pathToField.
		 * we detrmine wheather to create a new object or array we look into the type of the next element in the
		 * _props chain. If it's a string then it must be an object.
		 */
		if (p === '$') {
			if (typeof _props[0] === 'number') pathTofield.push([]);
			else pathTofield.push({});
			pathTofield = pathTofield[pathTofield.length - 1];
		} else pathTofield = pathTofield[p];
		p = _props.shift();
	}
	return false;
}

export function isObjectsEqual(obj1, obj2) {
	if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;
	for (let key of Object.keys(obj1)) {
		if (obj1[key] && obj1[key].constructor.name === 'Object') {
			if (obj2[key] && obj2[key].constructor.name === 'Object') {
				if (!isObjectsEqual(obj1[key], obj2[key])) return false;
				continue;
			} else return false;
		}
		if (obj1[key] === obj2[key]) continue;
		return false;
	}
	return true;
}

export function nullCoalesc(...vals) {
	if (vals.length <= 1) return vals;
	let args = vals.slice(0, -1),
		default_val = vals.slice(-1);

	for (let arg of args) {
		if (arg !== undefined && arg !== null) return arg;
	}
	return default_val;
}

export function transfereObject(src, dst, blackList: any = null) {
	let newObj = {};
	Object.keys(src).forEach(k => {
		if (blackList && Array.isArray(blackList) && blackList.includes(k)) newObj[k] = src[k];
		else newObj[k] = dst[k] || src[k];
	});
	return newObj;
}

export function extractEventValue(e) {
	if (e?.target) {
		if (
			e.target.attributes?.type?.value === 'checkbox' ||
			e.target.type === 'checkbox' ||
			e.nativeEvent?.target.attributes.type?.value === 'checkbox'
		) {
			return e.target.checked;
		} else if (e.target.value !== null) return e.target.value;
		else return '';
	} else if (e === undefined || e === null) return '';
	return e;
}
// export function getObjectProperty(obj, prop) {
//     let props = prop.split('.');
//     if(props.length < 1) return false;
//     let data = obj;

//     // check if any of the props are array indecies, e.g., a.b[1].c, it should enter here like ['a', 'b[1]', 'c'] and leaves
//     // like ['a', 'b', 1, 'c']
//     let _props = [];
//     for(let i in props){
//         let _p = props[i].match(constants.extractArrayIndexPattern);
//         if(_p){
//             let name = props[i].split(_p[0])[0];
//             _props.push(name);
//             _props = _props.concat(_p.map(a => {
//                 a = a.split('').slice(1, -1).join('');
//                 let parsed = parseInt(a);
//                 if(isNaN(parsed) && a === '$') return a;
//                 else if (!isNaN(parsed)) return parsed;
//                 return '';
//             }))
//         } else _props.push(props[i]);
//     }

//     let pathTofield = data, p = _props.shift();

//     while(pathTofield && p !== "" && pathTofield.hasOwnProperty(p)) {
//         if(_props.length === 0 ) { //last prop.
//             return pathTofield[p] ;
//         }

//         p = _props.shift();

//     }
//     return null;
// }

export function deepArrayCmp(arrA, arrB) {
	return arrA.every((item, index) => {
		if (item.constructor.name === 'Object') {
			const innerDiff = deepDiff(item, arrB[index]);
			return !Object.keys(innerDiff).length;
		} else if (Array.isArray(item)) {
			if (!Array.isArray(arrB[index]) || item.length !== arrB[index].length) {
				return false;
			} else {
				return deepArrayCmp(item, arrB[index]);
			}
		} else if (item !== arrB[index]) {
			return false;
		}
		return true;
	});
}

export function deepDiff(objA, objB) {
	return Object.keys(objA).reduce((diff, key) => {
		if (objA[key].constructor.name === 'Object') {
			const innerDiff = deepDiff(objA[key], objB[key]);
			if (Object.keys(innerDiff).length) {
				diff[key] = innerDiff;
			}
		} else if (Array.isArray(objA[key])) {
			if (!Array.isArray(objB[key]) || objA[key].length !== objB[key].length) {
				diff[key] = objA[key];
			} else if (!deepArrayCmp(objA[key], objB[key])) {
				diff[key] = objA[key];
			}
		} else if (objA[key] !== objB[key]) {
			diff[key] = objA[key];
		}
		return diff;
	}, {});
}

export const cleanUndefined = obj => {
	const newObj = {};

	Object.keys(obj).forEach(key => {
		if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
			newObj[key] = cleanUndefined(obj[key]); // recurse
		} else if (obj[key] !== undefined) {
			newObj[key] = obj[key]; // copy value
		}
	});

	return newObj;
};
